import { useState, useEffect, useCallback } from 'react';
import api, { apiUrl } from '../utils/apiClient';

const SW_PATH = '/push-sw.js';

const isCapacitorNative = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); }
  catch { return false; }
};

// Ensure the dedicated push service worker is registered (or already registered).
// Returns the active registration, or null if SW isn't supported / native.
const ensurePushWorker = async () => {
  if (isCapacitorNative()) return null;
  if (!('serviceWorker' in navigator)) return null;
  let reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) reg = await navigator.serviceWorker.register(SW_PATH);
  await navigator.serviceWorker.ready;
  return reg;
};

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = !isCapacitorNative() && 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    if (supported) {
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  // Check current subscription status
  const checkSubscription = async () => {
    try {
      const registration = await ensurePushWorker();
      if (!registration) { setLoading(false); return; }
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) {
        setSubscription(existingSub);
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      // Get VAPID public key from server
      const keyResponse = await api.get(apiUrl('/push/vapid-key'));
      const vapidPublicKey = keyResponse.data.key;
      if (!vapidPublicKey) throw new Error('Push notifications not configured on server');

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      const registration = await ensurePushWorker();

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subscriptionData = {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(pushSubscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(pushSubscription.getKey('auth'))))
        }
      };

      await api.post(apiUrl('/push/subscribe'), subscriptionData);

      setSubscription(pushSubscription);
      setIsSubscribed(true);

      return true;
    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return false;

    try {
      setLoading(true);
      setError(null);

      // Unsubscribe on server
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      };

      await api.post(apiUrl('/push/unsubscribe'), subscriptionData);

      // Unsubscribe locally
      await subscription.unsubscribe();

      setSubscription(null);
      setIsSubscribed(false);

      return true;
    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const response = await api.post(apiUrl('/push/test'));
      return response.data;
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message);
      return null;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
};

export default usePushNotifications;
